import { z } from "zod";

/**
 * UC-007 Phase 20 — Admin anomaly resolution schemas (REQ-SEC-019).
 *
 * Server-side (PaymentAnomalyService) vyžaduje reason ≥ 10 znaků a action
 * v whitelistu (confirmed_match | rejected_match | manual_pair | marked_for_refund | ignored).
 * Pre-submit Zod validation povinné — bez ní Slide-over Form se neodešle.
 */

export const ANOMALY_RESOLUTION_ACTIONS = [
  "confirmed_match",
  "rejected_match",
  "manual_pair",
  "marked_for_refund",
  "ignored",
] as const;
export type AnomalyResolutionAction = (typeof ANOMALY_RESOLUTION_ACTIONS)[number];

export const RESOLUTION_REASON_MIN_LENGTH = 10;

export const anomalyResolutionSchema = z.object({
  action: z.enum(ANOMALY_RESOLUTION_ACTIONS, {
    message: "Vyberte akci",
  }),
  reason: z
    .string()
    .trim()
    .min(
      RESOLUTION_REASON_MIN_LENGTH,
      `Důvod musí mít alespoň ${RESOLUTION_REASON_MIN_LENGTH} znaků.`,
    ),
});
export type AnomalyResolutionInput = z.infer<typeof anomalyResolutionSchema>;

export const ANOMALY_TYPES = [
  "heuristic_proposed",
  "unmatched",
  "cross_account",
  "duplicate",
  "amount_mismatch",
] as const;
export type AnomalyType = (typeof ANOMALY_TYPES)[number];

export const ANOMALY_SEVERITIES = [
  "critical",
  "high",
  "medium",
  "low",
] as const;
export type AnomalySeverity = (typeof ANOMALY_SEVERITIES)[number];

export interface AnomalyRow {
  anomaly_id: string;
  anomaly_type: AnomalyType;
  severity: AnomalySeverity;
  status: "pending" | "resolved";
  tenant_id: string | null;
  fio_transaction_id: string;
  payment_intent_id: string | null;
  score: number | null;
  amount_kc: number;
  variable_symbol: string | null;
  counter_party_name: string | null;
  detail: unknown;
  resolution_action: AnomalyResolutionAction | null;
  resolution_reason: string | null;
  resolved_by_user_id: string | null;
  resolved_at: string | null;
  created_at: string;
}

export interface AnomalyListResponse {
  rows: AnomalyRow[];
}

export interface ResolveAnomalyResponse {
  anomaly_id: string;
  status: "resolved";
  resolved_at: string;
}

/**
 * SSE wire format z AlertsSseChannel (backend).
 */
export interface AdminAlertEvent {
  alert_id: string;
  kind: "anomaly";
  severity: AnomalySeverity;
  title: string;
  message: string;
  timestamp: string;
  anomaly_id?: string;
  anomaly_type?: AnomalyType;
}
